import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, ChefHat, Clock, Flame, Trash2, Edit, Filter, Sparkles, Loader2, Share2, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import RecipeImage from '../components/RecipeImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['all', 'lunch', 'dinner_main', 'dinner_secondary'];
const SCROLL_KEY = 'recipesScrollY';

export default function RecipesPage() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [estimatingId, setEstimatingId] = useState(null);
  // Recipes whose uploaded image file no longer exists (lost on a redeploy without a volume)
  const [brokenIds, setBrokenIds] = useState(new Set());
  const [fixingImages, setFixingImages] = useState(false);

  const markBrokenImage = (recipe) => {
    if (!(recipe.image || '').includes('/api/uploads/')) return; // only our own files are fixable
    setBrokenIds(prev => (prev.has(recipe.recipe_id) ? prev : new Set(prev).add(recipe.recipe_id)));
  };

  const handleFixImages = async () => {
    setFixingImages(true);
    try {
      const res = await fetch(`${API}/recipes/fix-broken-images`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || t('errors.generic')); return; }
      if (d.regenerated > 0) {
        toast.success(language === 'es'
          ? `${d.regenerated} imagen(es) regenerada(s) con IA`
          : `${d.regenerated} image(s) regenerated with AI`);
      } else if (d.broken > 0) {
        toast.message(language === 'es'
          ? `${d.broken} enlace(s) roto(s) limpiado(s). ${d.can_regenerate ? 'No se pudo regenerar; reinténtalo.' : 'Añade saldo a OpenAI para regenerarlas.'}`
          : `${d.broken} broken link(s) cleared. ${d.can_regenerate ? 'Could not regenerate; try again.' : 'Add OpenAI credit to regenerate them.'}`);
      } else {
        toast.success(language === 'es' ? 'No hay imágenes rotas' : 'No broken images');
      }
      setBrokenIds(new Set());
      fetchRecipes();
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setFixingImages(false);
    }
  };

  // Share: shareTarget = a recipe (share one) or 'all'
  const [shareTarget, setShareTarget] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    if (!shareEmail.trim()) return;
    setSharing(true);
    try {
      const res = await fetch(`${API}/recipes/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: shareEmail.trim(),
          recipe_ids: shareTarget === 'all' ? [] : [shareTarget.recipe_id],
        }),
      });
      const d = await res.json();
      if (res.ok) {
        const parts = [
          language === 'es' ? `${d.shared} receta(s) compartida(s)` : `${d.shared} recipe(s) shared`,
        ];
        if (d.skipped) parts.push(language === 'es' ? `${d.skipped} ya la(s) tenía` : `${d.skipped} already had`);
        if (d.limit_reached) parts.push(language === 'es' ? 'alcanzó su límite de plan' : 'reached their plan limit');
        toast.success(parts.join(' · '));
        setShareTarget(null);
        setShareEmail('');
      } else {
        toast.error(d.detail || t('errors.generic'));
      }
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setSharing(false);
    }
  };

  const handleEstimateMacros = async (recipe, e) => {
    e.stopPropagation();
    setEstimatingId(recipe.recipe_id);
    try {
      const res = await fetch(`${API}/recipes/${recipe.recipe_id}/estimate-macros`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const updated = await res.json();
        setRecipes(prev => prev.map(r => (r.recipe_id === recipe.recipe_id ? updated : r)));
        toast.success(language === 'es' ? 'Macros estimados con IA' : 'Macros estimated with AI');
      } else {
        const er = await res.json().catch(() => ({}));
        toast.error(er.detail || t('errors.generic'));
      }
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setEstimatingId(null);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, []);

  // Come back from a recipe where you left off
  useEffect(() => {
    if (loading) return;
    const saved = parseInt(sessionStorage.getItem(SCROLL_KEY) || '0', 10);
    if (saved > 0) {
      // Wait for the grid to paint before restoring
      requestAnimationFrame(() => window.scrollTo(0, saved));
      sessionStorage.removeItem(SCROLL_KEY);
    }
  }, [loading]);

  const openRecipe = (recipeId) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    navigate(`/recipes/${recipeId}`);
  };

  const fetchRecipes = async () => {
    try {
      const response = await fetch(`${API}/recipes`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRecipes(data);
      }
    } catch (error) {
      console.error('Error fetching recipes:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!recipeToDelete) return;

    try {
      const response = await fetch(`${API}/recipes/${recipeToDelete.recipe_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setRecipes(recipes.filter(r => r.recipe_id !== recipeToDelete.recipe_id));
        toast.success(t('success.deleted'));
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error(t('errors.generic'));
    } finally {
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
    }
  };

  // Filter + sort alphabetically (accent/case-insensitive)
  const filteredRecipes = recipes
    .filter(recipe => {
      const matchesSearch = recipe.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (recipe.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' ||
        (recipe.categories && recipe.categories.includes(selectedCategory)) ||
        recipe.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', language === 'en' ? 'en' : 'es', { sensitivity: 'base' }));

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h1 className="font-manrope font-bold text-3xl text-charcoal">
            {t('recipes.title')}
          </h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <Button
              data-testid="new-recipe-button"
              onClick={() => navigate('/recipes/new')}
              className="w-full sm:w-auto justify-center bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 font-manrope font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('recipes.add')}
            </Button>
            {recipes.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setShareTarget('all')}
                className="w-full sm:w-auto justify-center rounded-full px-5 py-5 border-primary text-primary hover:bg-primary/5 font-manrope font-bold"
                data-testid="share-all-recipes-button"
              >
                <Share2 className="w-5 h-5 mr-2" strokeWidth={1.5} />
                {language === 'es' ? 'Compartir todas' : 'Share all'}
              </Button>
            )}
          </div>
        </div>

        {/* Broken images (files lost on a redeploy): offer to clean/regenerate them */}
        {brokenIds.size > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4 mb-6">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {language === 'es'
                  ? `${brokenIds.size} receta(s) han perdido su imagen. Puedo limpiar los enlaces rotos y regenerarlas con IA.`
                  : `${brokenIds.size} recipe(s) lost their image. I can clear the broken links and regenerate them with AI.`}
              </span>
            </div>
            <Button
              onClick={handleFixImages}
              disabled={fixingImages}
              className="rounded-full bg-primary shrink-0 w-full sm:w-auto justify-center"
              data-testid="fix-images-button"
            >
              {fixingImages ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {fixingImages
                ? (language === 'es' ? 'Reparando...' : 'Fixing...')
                : (language === 'es' ? 'Reparar imágenes' : 'Fix images')}
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
            <Input
              data-testid="recipe-search-input"
              type="text"
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
            />
          </div>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger
              className="w-full sm:w-[200px] h-12 rounded-xl border-border bg-white/50 font-inter"
              data-testid="category-filter-select"
            >
              <Filter className="w-4 h-4 mr-2 text-stone" strokeWidth={1.5} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat} data-testid={`category-option-${cat}`}>
                  {t(`recipes.categories.${cat}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Recipes Grid */}
        {filteredRecipes.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <ChefHat className="w-12 h-12 text-sage" strokeWidth={1.5} />
            </div>
            <h2 className="font-manrope font-semibold text-2xl text-charcoal mb-2">
              {t('recipes.empty')}
            </h2>
            <p className="font-inter text-stone mb-8">
              {t('recipes.emptyHint')}
            </p>
            <Button
              data-testid="create-first-recipe-button"
              onClick={() => navigate('/recipes/new')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 font-manrope font-bold shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('recipes.add')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRecipes.map((recipe, index) => (
              <Card
                key={recipe.recipe_id}
                className="group relative overflow-hidden rounded-3xl bg-card border border-border/50 shadow-soft hover:shadow-lift transition-all duration-300 cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => openRecipe(recipe.recipe_id)}
                data-testid={`recipe-card-${recipe.recipe_id}`}
              >
                {/* Image */}
                <div className="aspect-[4/3] overflow-hidden">
                  <RecipeImage
                    src={recipe?.image}
                    alt={recipe?.name}
                    containerClassName="w-full h-full"
                    className="group-hover:scale-105"
                    onError={() => markBrokenImage(recipe)}
                  />
                </div>

                {/* Category Badges */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-1 max-w-[70%]">
                  {recipe.categories && recipe.categories.length > 0 ? (
                    recipe.categories.map(cat => (
                      <span key={cat} className="bg-white/90 backdrop-blur-sm text-charcoal font-manrope font-semibold text-[10px] uppercase tracking-wide px-2 py-1 rounded-full whitespace-nowrap">
                        {t(`recipes.categories.${cat}`)}
                      </span>
                    ))
                  ) : (
                    <span className="bg-white/90 backdrop-blur-sm text-charcoal font-manrope font-semibold text-xs uppercase tracking-wide px-3 py-1.5 rounded-full">
                      {t(`recipes.categories.${recipe.category}`)}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white"
                    onClick={(e) => { e.stopPropagation(); setShareTarget(recipe); }}
                    title={language === 'es' ? 'Compartir' : 'Share'}
                    data-testid={`share-recipe-${recipe.recipe_id}`}
                  >
                    <Share2 className="w-4 h-4 text-charcoal" strokeWidth={1.5} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/recipes/${recipe.recipe_id}/edit`);
                    }}
                    data-testid={`edit-recipe-${recipe.recipe_id}`}
                  >
                    <Edit className="w-4 h-4 text-charcoal" strokeWidth={1.5} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="w-9 h-9 rounded-full bg-red-100 backdrop-blur-sm text-red-500 hover:bg-destructive hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRecipeToDelete(recipe);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-recipe-${recipe.recipe_id}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-manrope font-semibold text-lg text-charcoal mb-2 line-clamp-2 break-words">
                    {recipe.name}
                  </h3>
                  {recipe.description && (
                    <p className="font-inter text-sm text-stone line-clamp-2 mb-4">
                      {recipe.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-sm">
                    {recipe.prep_time && (
                      <div className="flex items-center gap-1 text-stone">
                        <Clock className="w-4 h-4" strokeWidth={1.5} />
                        <span className="font-inter">{recipe.prep_time} {t('recipes.prepTime')}</span>
                      </div>
                    )}
                    {recipe.calories && (
                      <div className="flex items-center gap-1 text-sage">
                        <Flame className="w-4 h-4" strokeWidth={1.5} />
                        <span className="font-inter">
                          {recipe.calories} {t('recipes.calories')}
                          {recipe.calories_approximate && <span className="text-stone"> {t('recipes.approxCalories')}</span>}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Macros: show when present, offer to estimate when missing (Premium) */}
                  {(() => {
                    const hasMacros = recipe.protein_g != null && recipe.carbs_g != null && recipe.fat_g != null;
                    if (hasMacros) {
                      return (
                        <p className="mt-2 text-xs font-inter text-stone">
                          P{Math.round(recipe.protein_g)} · C{Math.round(recipe.carbs_g)} · G{Math.round(recipe.fat_g)}
                          <span className="text-stone/60"> (g)</span>
                        </p>
                      );
                    }
                    if (user?.plan === 'premium') {
                      return (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={estimatingId === recipe.recipe_id}
                          onClick={(e) => handleEstimateMacros(recipe, e)}
                          className="mt-3 rounded-full border-primary text-primary hover:bg-primary/5 text-xs h-8"
                          data-testid={`estimate-macros-${recipe.recipe_id}`}
                        >
                          {estimatingId === recipe.recipe_id
                            ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                          {estimatingId === recipe.recipe_id
                            ? (language === 'es' ? 'Estimando...' : 'Estimating...')
                            : (language === 'es' ? 'Estimar macros' : 'Estimate macros')}
                        </Button>
                      );
                    }
                    return null;
                  })()}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Share Dialog */}
        <Dialog open={!!shareTarget} onOpenChange={(o) => { if (!o) { setShareTarget(null); setShareEmail(''); } }}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {language === 'es' ? 'Compartir recetas' : 'Share recipes'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-4">
              <p className="text-sm text-stone font-inter">
                {shareTarget === 'all'
                  ? (language === 'es'
                    ? `Se enviará una copia de tus ${recipes.length} recetas al usuario que indiques.`
                    : `A copy of your ${recipes.length} recipes will be sent to the user you choose.`)
                  : (language === 'es'
                    ? `Se enviará una copia de «${shareTarget?.name}».`
                    : `A copy of “${shareTarget?.name}” will be sent.`)}
              </p>
              <div className="space-y-1">
                <Label className="text-sm text-stone">{language === 'es' ? 'Email del usuario' : 'User email'}</Label>
                <Input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="usuario@email.com"
                  className="h-12 rounded-xl"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleShare(); }}
                />
                <p className="text-xs text-stone/70 font-inter">
                  {language === 'es'
                    ? 'Debe estar registrado. Recibirá una copia propia: si la edita o la borra, a ti no te afecta. Las que ya tenga (mismo nombre) se omiten.'
                    : 'They must be registered. They get their own copy: editing or deleting it does not affect you. Recipes they already have (same name) are skipped.'}
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShareTarget(null)} disabled={sharing} className="rounded-full">
                {t('cancel')}
              </Button>
              <Button onClick={handleShare} disabled={sharing || !shareEmail.trim()} className="rounded-full bg-primary">
                {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                {language === 'es' ? 'Compartir' : 'Share'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {t('delete')} "{recipeToDelete?.name}"
              </DialogTitle>
            </DialogHeader>
            <p className="font-inter text-stone py-4">
              {t('recipes.deleteConfirm')}
            </p>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="rounded-full"
                data-testid="cancel-delete-button"
              >
                {t('cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="rounded-full"
                data-testid="confirm-delete-button"
              >
                {t('delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
